////////// ONLINE ORDERS //////////

import {
    IGlobalOrderDetails,
    IOnlineOrderHistory,
    IOnlineOrderItems,
    IRetailOrderHistory,
    IRetailOrderDetails,
    OrderDetailsContactLenses,
    IRetailOrderItems,
} from '@interfaces/myPurchasesInterfaces';
import { getStringFromDate } from '@utils/dateUtils';
import { getOrderedRxcReviewObject, isValidJson } from '@utils/utils';
import { cloneDeep } from 'lodash';

export const mapOnlineOrdersHistory = (data: any): IOnlineOrderHistory[] => {
    return data?.map((item: any) => mapOnlineOrder(item)) ?? [];
};

export const mapOnlineOrder = (data: any): IOnlineOrderHistory => {
    return {
        orderId: data?.orderId,
        orderAddressEmail: data?.x_orderAddressEmail,
        ocrAddressId: data?.x_ocrAddressId,
        hasContactLenses: data?.x_hasContactLenses,
        placedDate: data?.placedDate,
        orderStatus: data?.orderStatus,
        grandTotalCurrency: data?.grandTotalCurrency
            ? ['USD', 'CAD'].includes(data.grandTotalCurrency.toUpperCase())
                ? '$'
                : data.grandTotalCurrency
            : '',
        grandTotal: data?.grandTotal,
        isPrescriptionRequired: data?.x_isPrescriptionRequired === 'true' ? true : false,
        isPrescriptionProvided: data?.x_isPrescriptionProvided === 'true' ? true : false,
        orderFormedBy: data?.x_orderFormedBy,
        isExchangeOrder: data?.x_isExchangeOrder === 'true' ? true : false,
        allItemsHaveSameStatusGroupAndEdd: data?.x_allItemsHaveSameStatusGroupAndEdd === 'true',
        allItemsEdd: data?.x_allOrderItemsEdd ?? '',
        allOrderItemsStatusGroup: data?.x_allOrderItemsStatusGroup ?? '',
    };
};

export const mapRetailOrderHistory = (data: any): IRetailOrderHistory[] => {
    return data?.map((item: any) => mapRetailOrder(item)) ?? [];
};

export const mapRetailOrder = (data: any): IRetailOrderHistory => {
    return {
        orderId: data?.order_id,
        orderStatus: !!data?.order_tracker_raw_status ? data?.order_tracker_raw_status : data?.order_status,
        placedDate: data?.event_date,
        grandTotal: data?.total_tender_value,
        retailOrderDetails: mapGlobalOrderRetailDetails(data, !!data?.is_only_lenses ? data?.is_only_lenses : "false", !!data?.sameParent ? data?.sameParent : "false"),
        isOnlyLenses: !!data?.is_only_lenses ? data?.is_only_lenses : "false",
        sameParent: !!data?.sameParent ? data?.sameParent : "false",
        isRetail: true
    };
};

export const mapGlobalOrderRetailDetails = (data: any, isOnlyLens: string, sameParent: string): IRetailOrderDetails => {
    const contactsItem = data?.line_items.filter(element => element.product_kind === 'CONTACTS')
    const hasContactLenses = contactsItem.length > 0;
    const isContacLensesItemTypeSales = hasContactLenses ? contactsItem[0].line_item_type.toLowerCase() === "sales" : false;

    return{
        orderNumber: data?.order_id,
        orderDate: data?.event_date,
        orderStatus: !!data?.order_tracker_raw_status ? data?.order_tracker_raw_status : data?.order_status,
        paymentMethod: data?.payment_method,
        orderTotal: data?.total_tender_value,
        orderItems: matchesOrderRetailItems(data?.line_items, isOnlyLens, sameParent) ?? [],
        store: {
            storeName: data?.store?.store_name,
            address: data?.store?.address,
            phone: data?.store?.phone,
            city:  data?.store?.city,
            province:  data?.store?.state_province,
            country:  data?.store?.country,
            postalCode:  data?.store?.postal_code,
            number:  data?.store?.store_number,
            mapImpage: data?.store?.static_map_Image
        },
        hasContactLenses: hasContactLenses,
        isItemTypeSales: isContacLensesItemTypeSales, 
        prescription : {
            prescriptionId: data?.prescription_id,
            examExpireDate: data?.expire_date
        },
        isTrackItem: data?.isTrackItem
    }
}

export const matchesOrderRetailItems = (data: any, isOnlyLens: string, sameParent: string): IRetailOrderItems[] => {
    const arr1 = cloneDeep(data);
    const contactLensesItems = arr1?.filter(element => element.product_kind === 'CONTACTS');
    const otherItems = arr1?.filter(element => element.product_kind !== 'LENSES' && element.product_kind !== 'FRAMES' && element.product_kind !== 'CONTACTS' && element.product_kind !== 'WARRANTY' && element.product_kind !== 'SUN');
    let lensesItem = [];
    let warranty = [];
    let frameItem = [];
    let arr3: IRetailOrderItems[] = [];

    if(contactLensesItems.length === 0) {
        lensesItem = arr1?.filter(element => element.product_kind === 'LENSES');
        warranty = arr1?.filter(element => element.product_kind === 'WARRANTY');

        if(isOnlyLens.includes('false')){
            frameItem = arr1?.filter(element => element.product_kind === 'FRAMES');
            if(frameItem.length === 0){
                frameItem = arr1?.filter(element => element.product_kind === 'SUN');
            }
        }

        const itemHasWarranty = warranty.length > 0;

        if(lensesItem.length > 0 || frameItem.length > 0){
            arr3.push({
                orderItemStatus: isOnlyLens.includes('true') || frameItem.length === 0 ? lensesItem[0]?.order_item_status : frameItem[0]?.order_item_status,
                picture: isOnlyLens.includes('true') || frameItem.length === 0 ? lensesItem[0]?.picture : frameItem[0]?.picture,
                pdpLink: isOnlyLens.includes('true') || frameItem.length === 0 ? "" : frameItem[0]?.pdp_link,
                productKind: isOnlyLens.includes('true') || frameItem.length === 0 ? lensesItem[0]?.product_kind : frameItem[0]?.product_kind,
                lineItemType: isOnlyLens.includes('true') || frameItem.length === 0 ? lensesItem[0]?.line_item_type : frameItem[0]?.line_item_type,
                isOnlyLens: isOnlyLens.includes('true'),
                isPublished: frameItem[0]?.isPublished,

                //Custom Frame information
                isCustom: frameItem[0]?.isCustom,
                subCategory: frameItem[0]?.isCustom ? frameItem[0]?.subcategory : null,
                description: frameItem[0]?.isCustom ? frameItem[0]?.description : null,
                brandDescription: frameItem[0]?.isCustom ? frameItem[0]?.brandDescription : null,

                //Frame Informations
                frameColor: frameItem[0]?.frame_color,
                frameName: frameItem[0]?.frame_name, 
                frameSize: frameItem[0]?.size,

                //Lens Information
                lensBrand: lensesItem[0]?.lensBrand,
                lensBundle: lensesItem[0]?.lensBundle,
                lensType: lensesItem[0]?.lensType,
                thickness: lensesItem[0]?.thickness,
                visionNeed: lensesItem[0]?.visionNeed,
                finishings: lensesItem[0]?.finishings,
                color: lensesItem[0]?.color,
                lensName: lensesItem[0]?.lens_name,

                //Warranty on item
                hasWarranty: itemHasWarranty,
                validUntil: itemHasWarranty ? warranty[0].valid_until : null,
            })
        }
    } else {
        if(contactLensesItems.length === 1){
            const isRight = contactLensesItems[0]?.eye_code.includes('RIGHT');

            arr3.push({
                orderItemStatus: contactLensesItems[0]?.order_item_status,
                picture: contactLensesItems[0]?.picture,
                pdpLink: contactLensesItems[0]?.pdp_link,
                productKind: contactLensesItems[0]?.product_kind,
                lineItemType: contactLensesItems[0]?.line_item_type,

                catentryIdRight: isRight ? contactLensesItems[0]?.catentryId : null,
                catentryIdLeft: isRight ? null : contactLensesItems[0]?.catentryId,
                parentCatentryId: contactLensesItems[0]?.parentCatentryId,
                parentUpc: contactLensesItems[0]?.parentUPC,
                dataPrescription: {
                    rightCyl: contactLensesItems[0]?.dataPrescription?.rxRightCyl,
                    rightColor: contactLensesItems[0]?.dataPrescription?.rxRightColor,
                    rightAxis: contactLensesItems[0]?.dataPrescription?.rxRightAxis,
                    rightPower: contactLensesItems[0]?.dataPrescription?.rxRightPower,
                    rightDia: contactLensesItems[0]?.dataPrescription?.rxRightDia,
                    rightBc: contactLensesItems[0]?.dataPrescription?.rxRightBc,
                    rightDominance: contactLensesItems[0]?.dataPrescription?.rxRightDominance,
                    leftCyl: contactLensesItems[0]?.dataPrescription?.rxLeftCyl,
                    leftColor: contactLensesItems[0]?.dataPrescription?.rxLeftColor,
                    leftAxis: contactLensesItems[0]?.dataPrescription?.rxLeftAxis,
                    leftPower: contactLensesItems[0]?.dataPrescription?.rxLeftPower,
                    leftDia: contactLensesItems[0]?.dataPrescription?.rxLeftDia,
                    leftBc: contactLensesItems[0]?.dataPrescription?.rxLeftBc,
                    leftDominance: contactLensesItems[0]?.dataPrescription?.rxLeftDominance,

                },
                brandName: contactLensesItems[0]?.brandName,
                styleName: contactLensesItems[0]?.styleName,
                quantityRight: isRight ? contactLensesItems[0]?.quantity : null,
                quantityLeft: isRight ? null : contactLensesItems[0]?.quantity,
                eyeCode: contactLensesItems[0]?.eye_code,
                buyable: contactLensesItems[0]?.buyable,
                isPublished: contactLensesItems[0]?.isPublished
            })
        } else {
            if(sameParent === "true") {
                const clRight = contactLensesItems[0]?.eye_code.includes('RIGHT') ? contactLensesItems[0] : contactLensesItems[1];
                const clLeft = contactLensesItems[0]?.eye_code.includes('LEFT') ? contactLensesItems[0] : contactLensesItems[1];

                arr3.push({
                    orderItemStatus: contactLensesItems[0]?.order_item_status,
                    picture: contactLensesItems[0]?.picture,
                    pdpLink: contactLensesItems[0]?.pdp_link,
                    productKind: contactLensesItems[0]?.product_kind,
                    lineItemType: contactLensesItems[0]?.line_item_type,

                    catentryIdRight: clRight.catentryId,
                    catentryIdLeft: clLeft.catentryId,
                    parentCatentryId: contactLensesItems[0]?.parentCatentryId,
                    parentUpc: contactLensesItems[0]?.parentUPC,
                    dataPrescription: {
                        rightCyl: clRight.dataPrescription?.rxRightCyl,
                        rightColor: clRight.dataPrescription?.rxRightColor,
                        rightAxis: clRight.dataPrescription?.rxRightAxis,
                        rightPower: clRight.dataPrescription?.rxRightPower,
                        rightDia: clRight.dataPrescription?.rxRightDia,
                        rightBc: clRight.dataPrescription?.rxRightBc,
                        rightDominance: clRight.dataPrescription?.rxRightDominance,
                        leftCyl: clLeft.dataPrescription?.rxLeftCyl,
                        leftColor: clLeft.dataPrescription?.rxLeftColor,
                        leftAxis: clLeft.dataPrescription?.rxLeftAxis,
                        leftPower: clLeft.dataPrescription?.rxLeftPower,
                        leftDia: clLeft.dataPrescription?.rxLeftDia,
                        leftBc: clLeft.dataPrescription?.rxLeftBc,
                        leftDominance: clLeft.dataPrescription?.rxLeftDominance,

                    },
                    brandName: contactLensesItems[0]?.brandName,
                    styleName: contactLensesItems[0]?.styleName,
                    quantityRight: clRight.quantity,
                    quantityLeft: clLeft.quantity,
                    eyeCode: 'LEFT_AND_RIGHT',
                    buyable: contactLensesItems[0]?.buyable,
                    isPublished: contactLensesItems[0]?.isPublished,
                })
            } else {
                for(let i = 0; i < contactLensesItems.length; i++){
                    const isRight = contactLensesItems[i]?.eye_code.includes('RIGHT');
                    arr3.push({
                        orderItemStatus: contactLensesItems[i]?.order_item_status,
                        picture: contactLensesItems[i]?.picture,
                        pdpLink: contactLensesItems[i]?.pdp_link,
                        productKind: contactLensesItems[i]?.product_kind,
                        lineItemType: contactLensesItems[i]?.line_item_type,

                        catentryIdRight: isRight ? contactLensesItems[i]?.catentryId : null,
                        catentryIdLeft: isRight ? null : contactLensesItems[i]?.catentryId,
                        parentCatentryId: contactLensesItems[i]?.parentCatentryId,
                        parentUpc: contactLensesItems[0]?.parentUPC,
                        dataPrescription: {
                            rightCyl: contactLensesItems[i]?.dataPrescription?.rxRightCyl,
                            rightColor: contactLensesItems[i]?.dataPrescription?.rxRightColor,
                            rightAxis: contactLensesItems[i]?.dataPrescription?.rxRightAxis,
                            rightPower: contactLensesItems[i]?.dataPrescription?.rxRightPower,
                            rightDia: contactLensesItems[i]?.dataPrescription?.rxRightDia,
                            rightBc: contactLensesItems[i]?.dataPrescription?.rxRightBc,
                            rightDominance: contactLensesItems[i]?.dataPrescription?.rxRightDominance,
                            leftCyl: contactLensesItems[i]?.dataPrescription?.rxLeftCyl,
                            leftColor: contactLensesItems[i]?.dataPrescription?.rxLeftColor,
                            leftAxis: contactLensesItems[i]?.dataPrescription?.rxLeftAxis,
                            leftPower: contactLensesItems[i]?.dataPrescription?.rxLeftPower,
                            leftDia: contactLensesItems[i]?.dataPrescription?.rxLeftDia,
                            leftBc: contactLensesItems[i]?.dataPrescription?.rxLeftBc,
                            leftDominance: contactLensesItems[i]?.dataPrescription?.rxLeftDominance,
                        },
                        brandName: contactLensesItems[i]?.brandName,
                        styleName: contactLensesItems[i]?.styleName,
                        quantityRight: isRight ? contactLensesItems[i]?.quantity : null,
                        quantityLeft: isRight ? null : contactLensesItems[i]?.quantity,
                        eyeCode: contactLensesItems[i]?.eye_code,
                        buyable: contactLensesItems[i]?.buyable,
                        isPublished: contactLensesItems[i]?.isPublished
                    })
                }
            }
        }
    }

    for(const element of otherItems){
        arr3.push({
            orderItemStatus: element.order_item_status,
            picture: element.picture,
            productKind: element.product_kind,
            lineItemType: element.line_item_type,
            description: element.description,
            brandDescription: element.brandDescription,
            subCategory: element.subcategory
        })
    }

    return arr3
}

export const mapGlobalOrderDetails = (data: any): IGlobalOrderDetails => {
    return {
        orderNumber: data?.orderId,
        orderDate: data?.placedDate
            ? getStringFromDate(new Date(data.placedDate), 'MM/dd/yyyy')
            : null,
        orderStatus: data?.orderStatus,
        shippingMethod:
            data?.orderItem?.[0].shipModeDescription === 'Collect in store'
                ? `${data?.orderItem?.[0].shipModeDescription} at ${data?.orderItem?.[0]?.firstName} ${data?.orderItem?.[0]?.lastName}, ${data?.orderItem?.[0]?.addressLine?.[0]}, ${data?.orderItem?.[0]?.city}, ${data?.orderItem?.[0]?.state}, ${data?.orderItem?.[0]?.zipCode}, ${data?.orderItem?.[0]?.country}`
                : data?.orderItem?.[0]?.shipModeCode,
        shippingAddress: `${data?.orderItem?.[0]?.firstName} ${data?.orderItem?.[0]?.lastName}, ${data?.orderItem?.[0]?.addressLine?.[0]}, ${data?.orderItem?.[0]?.city}, ${data?.orderItem?.[0]?.state}, ${data?.orderItem?.[0]?.zipCode}, ${data?.orderItem?.[0]?.country}`,
        billingAddress: `${data?.paymentInstruction?.[0]?.firstName} ${data?.paymentInstruction?.[0]?.lastName}, ${data?.paymentInstruction?.[0]?.addressLine?.[0]}, ${data?.paymentInstruction?.[0]?.city}, ${data?.paymentInstruction?.[0]?.state}, ${data?.paymentInstruction?.[0]?.zipCode}, ${data?.paymentInstruction?.[0]?.country}`,
        paymentMethod: data?.paymentInstruction?.[0]?.piDescription,
        subtotal: `${
            data?.grandTotalCurrency
                ? ['USD', 'CAD'].includes(data.grandTotalCurrency.toUpperCase())
                    ? '$'
                    : data.grandTotalCurrency
                : ''
        } ${Number(
            Number(data?.grandTotal) -
                (Number(data?.totalShippingTax) + Number(data?.totalSalesTax))
        ).toFixed(2)}`,
        shippingCost: `${
            Number(data?.totalShippingCharge) === 0
                ? 'FREE'
                : `${
                      data?.totalShippingChargeCurrency
                          ? ['USD', 'CAD'].includes(data.totalShippingChargeCurrency.toUpperCase())
                              ? '$'
                              : data.totalShippingChargeCurrency
                          : ''
                  } ${Number(data?.totalShippingCharge).toFixed(2)}`
        }`,
        taxes: `${
            data?.totalShippingTaxCurrency
                ? ['USD', 'CAD'].includes(data.totalShippingTaxCurrency.toUpperCase())
                    ? '$'
                    : data.totalShippingTaxCurrency
                : ''
        } ${Number(data?.totalShippingTax) + Number(data?.totalSalesTax)}`,
        orderTotal: `${
            data?.grandTotalCurrency
                ? ['USD', 'CAD'].includes(data.grandTotalCurrency.toUpperCase())
                    ? '$'
                    : data.grandTotalCurrency
                : ''
        } ${Number(data?.grandTotal).toFixed(2)}`,
        readyForPickUpAddress:
            data?.orderItem?.every(item => item.orderItemStatus === 'U') &&
            `${data?.orderItem?.[0]?.firstName} ${data?.orderItem?.[0]?.lastName}, ${data?.orderItem?.[0]?.addressLine?.[0]}, ${data?.orderItem?.[0]?.city}, ${data?.orderItem?.[0]?.state}, ${data?.orderItem?.[0]?.zipCode}, ${data?.orderItem?.[0]?.country}`,
        readyForPickUpPhone:
            data?.orderItem?.every(item => item.orderItemStatus === 'U') &&
            `${data?.orderItem?.[0]?.phone1}`,
        quoteAddress:
            data?.orderDescription?.toUpperCase() === 'QUOTE' &&
            `${data?.x_store_name_quote}, ${
                data?.x_store_address_quote + data?.x_store_city_quote
            }, ${data?.x_store_state_quote} ${data?.x_store_zipcode_quote}`,
        orderItems: matchesOrderItems(data?.orderItem) ?? [],
        hasContactLenses:
            data?.orderItem?.some(item => ['LCON', 'RCON'].includes(item.comments)) ?? false,
        billingAddressId: +data?.paymentInstruction?.[0]?.billing_address_id,
        paymentMethodId: data?.paymentInstruction?.[0]?.payMethodId,
        isExchangeOrder: data?.x_isExchangeOrder === 'true' ? true : false
    };
};

export const mapGlobalOrderDetailsImages = (data: any) => {
    return matchesOrderItemsImages(data?.orderItem) ?? [];
};

export const mapGlobalOrderDetailsStatus = (data: any) => {
    return matchesOrderItemsStatus(data?.orderItem) ?? [];
};

export const mapOnlineOrderItem = (
    data: any,
    data1: any,
    isWarranty: boolean,
    isFullContactLenses: boolean,
    contactLens: any
): IOnlineOrderItems => {
    const insuranceAppliedCode = "INSADJ";

    return {
        priceEPP: data?.xitem_priceEPP,
        valid_until_EPP: data?.xitem_valid_until_EPP,
        orderItemId: data?.orderItemId,
        productId: isFullContactLenses
            ? [contactLens?.productId, data?.productId]
            : data?.productId,
        partNumber: data?.partNumber,
        productPageUrl: data?.xitem_productPageUrl,
        imgUrl: data?.xitem_img_url,
        orderStatus: data?.orderItemStatus,
        oldPrice: isFullContactLenses
            ? `${
                  data?.currency
                      ? ['USD', 'CAD'].includes(data.currency.toUpperCase())
                          ? '$'
                          : data.currency
                      : ''
              } ${(
                  Number(data?.orderItemPrice) -
                  Number(data?.totalAdjustment?.value) +
                  (Number(data?.orderItemPrice) - Number(data?.totalAdjustment?.value))
              ).toFixed(2)}`
            : `${
                  data?.currency
                      ? ['USD', 'CAD'].includes(data.currency.toUpperCase())
                          ? '$'
                          : data.currency
                      : ''
              } ${(Number(data?.orderItemPrice) - Number(data?.totalAdjustment?.value)).toFixed(
                  2
              )}`,
        newPrice: isFullContactLenses
            ? `${
                  data?.currency
                      ? ['USD', 'CAD'].includes(data.currency.toUpperCase())
                          ? '$'
                          : data.currency
                      : ''
              } ${(Number(data?.orderItemPrice) + Number(contactLens?.orderItemPrice)).toFixed(2)}`
            : `${
                  data?.currency
                      ? ['USD', 'CAD'].includes(data.currency.toUpperCase())
                          ? '$'
                          : data.currency
                      : ''
              } ${Number(data?.orderItemPrice).toFixed(2)}`,
        orderItemType: isFullContactLenses
            ? [contactLens?.comments, data?.comments]
            : data?.comments,
        lenses: {
            ...(data1 &&
                data1?.partNumber != '99' &&
                !!data.xitem_rxcReviewObject &&
                isValidJson(data.xitem_rxcReviewObject) &&
                getOrderedRxcReviewObject(data.xitem_rxcReviewObject)),
        },
        frame: ['LCON', 'RCON'].includes(data?.comments) ? ( data?.xitem_descriptiveName_pim ?? data?.xitem_descriptiveName) : data?.xitem_modelName_pim ?? data?.xitem_frame_product_name,
        color: !!data?.xitem_color_frame_name && data?.xitem_color_frame_name != "" ? data.xitem_color_frame_name : data?.xitem_frameColor_pim, size: data?.xitem_size_frame,
        brand:  data?.xitem_manufacturerName_pim ?? data?.xitem_manufacturerName ?? data?.xitem_cl_brand_name,
        insuranceBenefits: isFullContactLenses
        ? !!data?.adjustment &&
        !!contactLens?.adjustment &&
        contactLens.adjustment.some(adj => adj.code === insuranceAppliedCode)
          ? `${(function () {
                const dataAdjustment = data?.adjustment.find(adj => adj.code === insuranceAppliedCode);
                const contactLensAdjustment = contactLens?.adjustment.find(adj => adj.code === insuranceAppliedCode);
                const totalAmount =
                    Number(dataAdjustment?.amount ?? 0) +
                    Number(contactLensAdjustment?.amount ?? 0);
                const currencySymbol = dataAdjustment?.currency
                    ? ['USD', 'CAD'].includes(dataAdjustment.currency.toUpperCase())
                        ? '$'
                        : dataAdjustment.currency
                    : '';
                return totalAmount < 0
                    ? `- ${currencySymbol}${Math.abs(totalAmount).toFixed(2)}`
                    : `${currencySymbol}${totalAmount.toFixed(2)}`;
            })()}
             `
          : ``
      : data1
      ? !!data?.adjustment &&
        !!data1?.adjustment &&
        data1.adjustment.some(adj => adj.code === insuranceAppliedCode)
          ? `${(function () {
                const dataAdjustment = data?.adjustment.find(adj => adj.code === insuranceAppliedCode);
                const data1Adjustment = data1?.adjustment.find(adj => adj.code === insuranceAppliedCode);
                const totalAmount =
                    Number(dataAdjustment?.amount ?? 0) +
                    Number(data1Adjustment?.amount ?? 0);
                const currencySymbol = dataAdjustment?.currency
                    ? ['USD', 'CAD'].includes(dataAdjustment.currency.toUpperCase())
                        ? '$'
                        : dataAdjustment.currency
                    : '';
                return totalAmount < 0
                    ? `- ${currencySymbol}${Math.abs(totalAmount).toFixed(2)}`
                    : `${currencySymbol}${totalAmount.toFixed(2)}`;
            })()}
            `
          : ``
      : !!data.adjustment && data.adjustment.some(adj => adj.code === insuranceAppliedCode)
      ? `${(function () {
            const dataAdjustment = data?.adjustment.find(adj => adj.code === insuranceAppliedCode);
            const totalAmount = Number(dataAdjustment?.amount ?? 0);
            const currencySymbol = dataAdjustment?.currency
                ? ['USD', 'CAD'].includes(dataAdjustment.currency.toUpperCase())
                    ? '$'
                    : dataAdjustment.currency
                : '';
            return totalAmount < 0
                ? `- ${currencySymbol}${Math.abs(totalAmount).toFixed(2)}`
                : `${currencySymbol}${totalAmount.toFixed(2)}`;
        })()}`
      : ``,
        ...(['LCON', 'RCON'].includes(data?.comments) && {
            quantity: isFullContactLenses
                ? [contactLens?.quantity, data?.quantity]
                : data?.quantity,
        }),
        isPrescriptionRequired:
            data?.orderItemStatus === 'E' &&
            ((['LCON', 'RCON'].includes(data?.comments) && !data?.xitem_field2) ||
                (['LENS'].includes(data1?.comments) &&
                    data1?.partNumber !== '99' &&
                    !data1?.xitem_field2)),
        prescriptionId: ['LCON', 'RCON'].includes(data?.comments)
            ? data?.xitem_field2
            : data1?.xitem_field2,
        email1: data?.email1,
        orderItemInventoryStatus: data?.orderItemInventoryStatus,
        edd: data?.xitem_edd
    };
};

export const mapOnlineOrderItemImages = (data: any) => {
    return {
        orderItemId: data?.orderItemId,
        productPageUrl: data?.xitem_productPageUrl,
        imgUrl: data?.xitem_img_url,
    };
};

export const mapOnlineOrderItemStatus = (data: any) => {
    return {
        orderItemId: data?.orderItemId,
        orderStatus: data?.orderItemStatus,
    };
};

/**The below function map the contact lenses attributes */

export const mapContactLensesAttributes = (data: any): OrderDetailsContactLenses => {
    const { orderItemType, ...rest } = data;

    let obj: OrderDetailsContactLenses = { lcon: null, rcon: null };
    obj[orderItemType.toLowerCase()] = {
        power : rest['CL_SPHERE_POWER'] ?? rest['PRD_POWER'],
        bc: rest['CL_BASE_CURVE'] ?? rest['PRF_BASECURVE'],
        dia:  rest['CL_DIAMETER'] ?? rest['PRF_DIAMETER'],
        cyl: rest['CL_CYLINDER'] ?? rest['PRD_CYLINDER'],
        axis:  rest['CL_AXIS'] ?? rest['PRD_AXIS'],
        add:  rest['CL_ADDITION'] ?? rest['PRD_ADDITION_TXT'],
        color:  rest['CL_COLOR'] ?? rest['PRD_COLOR'],
        dominance:  rest['CL_DOMINANCE']==='True' ? 'D' : 'N'
    };

    return obj;
};

/**The below function compare the order items and adding property to the EYE, SUN, RCON and LCON types */

export const matchesOrderItems = (data: any): IOnlineOrderItems[] => {
    const arr1 = cloneDeep(data);
    const lensesItems = arr1?.filter(element => element.comments === 'LENS');
    const contactLensesItems = arr1?.filter(element => ['LCON', 'RCON'].includes(element.comments));
    let arr3: IOnlineOrderItems[] = [];

    /** The function below checks if exists a warranty associated to the glasses */

    function isWarranty(xitem_field2: string): boolean {
        return arr1?.some(
            item => item.comments === 'WARRANTY' && item.orderItemId === xitem_field2
        );
    }

    arr1?.forEach(orderItem => {
        /**For each orderItem of SUN or EYE type, find the related lenses and verifying that it's not already added to the arr3 array */
        if (['SUN', 'EYE'].includes(orderItem.comments)) {
            lensesItems?.forEach(lensItem => {
                if (orderItem.orderItemId === lensItem.xitem_field1) {
                    if (!arr3?.some(element => element.orderItemId === orderItem.orderItemId)) {
                        arr3.push(
                            mapOnlineOrderItem(
                                orderItem,
                                lensItem,
                                isWarranty(orderItem.xitem_field2),
                                false,
                                null
                            )
                        );
                    }
                }
            });
        }
        if (['LCON', 'RCON'].includes(orderItem.comments)) {
            contactLensesItems?.forEach(clItem => {
                if (
                    orderItem.orderItemId === clItem.xitem_field1 &&
                    clItem.orderItemId === orderItem.xitem_field1
                ) {
                    // check if products (parent CL) are different -> add incomplete cl to orderitems
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        if (orderItem.xitem_parentCatentryId !== clItem.xitem_parentCatentryId) {
                            arr3.push(mapOnlineOrderItem(orderItem, null, false, false, clItem));
                            arr3.push(mapOnlineOrderItem(clItem, null, false, false, orderItem));
                        }else{
                            arr3.push(mapOnlineOrderItem(orderItem, null, false, true, clItem));
                        }
                    }               
                }
                if (!clItem.xitem_field1) {
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        arr3.push(mapOnlineOrderItem(clItem, null, false, false, null));
                    }
                }
            });
        }
    });

    return arr3;
};

export const matchesOrderItemsImages = (data: any): IOnlineOrderItems[] => {
    const contactLensesItems = data?.filter(element => ['LCON', 'RCON'].includes(element.comments));
    let arr3 = [];

    data?.forEach(orderItem => {
        /**For each orderItem the imageUrl */
        if (['SUN', 'EYE'].includes(orderItem.comments)) {
            arr3.push(mapOnlineOrderItemImages(orderItem));
        }
        if (['LCON', 'RCON'].includes(orderItem.comments)) {
            contactLensesItems?.forEach(clItem => {
                if (
                    orderItem.orderItemId === clItem.xitem_field1 &&
                    clItem.orderItemId === orderItem.xitem_field1
                ) {
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        arr3.push(mapOnlineOrderItemImages(orderItem));
                    }

                    // check if products (parent CL) are different -> add image
                    if (orderItem.xitem_parentCatentryId !== clItem.xitem_parentCatentryId &&
                        !arr3?.some(element => element.orderItemId === clItem.orderItemId)
                    ) {
                        arr3.push(mapOnlineOrderItemImages(clItem));
                    }
                }
                if (!clItem.xitem_field1) {
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        arr3.push(mapOnlineOrderItemImages(clItem));
                    }
                }
            });
        }
    });

    return arr3;
};

export const matchesOrderItemsStatus = (data: any): IOnlineOrderItems[] => {
    const contactLensesItems = data?.filter(element => ['LCON', 'RCON'].includes(element.comments));
    let arr3 = [];

    data?.forEach(orderItem => {
        /**For each orderItem the imageUrl */
        if (['SUN', 'EYE'].includes(orderItem.comments)) {
            arr3.push(mapOnlineOrderItemStatus(orderItem));
        }
        if (['LCON', 'RCON'].includes(orderItem.comments)) {
            contactLensesItems?.forEach(clItem => {
                if (
                    orderItem.orderItemId === clItem.xitem_field1 &&
                    clItem.orderItemId === orderItem.xitem_field1
                ) {
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        arr3.push(mapOnlineOrderItemStatus(orderItem));
                    }
                }
                if (!clItem.xitem_field1) {
                    if (!arr3?.some(element => element.orderItemId === clItem.orderItemId)) {
                        arr3.push(mapOnlineOrderItemStatus(clItem));
                    }
                }
            });
        }
    });

    return arr3;
};

/** The below function sorts by date the online orders history  */

export const sortOnlineOrdersHistory = (
    onlineOrders: IOnlineOrderHistory[]
): IOnlineOrderHistory[] => {
    return onlineOrders.sort(
        (a, b) => new Date(b.placedDate).valueOf() - new Date(a.placedDate).valueOf()
    );
};

/** The below function sorts by date the retail orders history  */

export const sortRetailOrdersHistory = (
    retailOrders: IRetailOrderHistory[]
): IRetailOrderHistory[] => {
    return retailOrders.sort(
        (a, b) => new Date(b.placedDate).valueOf() - new Date(a.placedDate).valueOf()
    );
};


/** The function below calls an external service that redirects to the OrderPaymentView page (payment page). */

export const handleContactLensesReorder = (
    orderId: number,
    storeId: number,
    catalogId: number,
    langId: number,
    otherParam: number
) => window['ocr'].performReorder(orderId, storeId, catalogId, langId, otherParam);
